import React, { useEffect } from 'react'

// Keep in sync with apps/mobile/src/components/OutOfScopeDrawer.tsx
export const QUALIFYING_EXAMPLE_PROMPTS: readonly string[] = [
  "Diligence on Stripe's 2026 unit economics and path to profitability",
  'Regulatory and pricing risk for GLP-1 drugs through 2027',
  'Competitive teardown of Databricks vs Snowflake for enterprise AI workloads',
]

export interface OutOfScopeModalProps {
  open: boolean
  message: string
  onClose: () => void
  onTryAgain: () => void
}

const OutOfScopeModal: React.FC<OutOfScopeModalProps> = ({
  open,
  message,
  onClose,
  onTryAgain,
}) => {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/40 overflow-y-auto overscroll-contain"
      role="dialog"
      aria-modal="true"
      aria-labelledby="out-of-scope-title"
      data-testid="out-of-scope-modal"
    >
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-lg p-6 md:p-8 border border-gray-200/50 dark:border-gray-700/50 max-h-[95dvh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-gray-700 dark:text-gray-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m8-9h1M3 12H2m15.364-6.364l.707-.707M5.636 18.364l-.707.707m12.728 0l.707.707M5.636 5.636l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z"
                />
              </svg>
            </div>
            <div>
              <h2
                id="out-of-scope-title"
                className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white"
              >
                Outside our research scope
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{message}</p>

        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
          We go deep on things like:
        </p>

        <ul className="space-y-2 mb-6" aria-label="Qualifying example prompts">
          {QUALIFYING_EXAMPLE_PROMPTS.map((example) => (
            <li
              key={example}
              className="flex items-start gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-4 py-3"
            >
              <svg
                className="w-4 h-4 flex-shrink-0 text-indigo-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3l1.5 4.5L11 9l-4.5 1.5L5 15l-1.5-4.5L-1 9l4.5-1.5L5 3zm14 5l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z"
                />
              </svg>
              <span className="text-sm text-gray-900 dark:text-gray-100 leading-snug">{example}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Got it
          </button>
          <button
            type="button"
            onClick={onTryAgain}
            className="px-5 py-3 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold hover:bg-gray-800 dark:hover:bg-gray-100"
          >
            Try a different topic
          </button>
        </div>
      </div>
    </div>
  )
}

export default OutOfScopeModal
